import { useTranslation } from "react-i18next";
import type { MessageInstance } from "antd/es/message/interface";
import * as XLSX from "xlsx";

interface FinanceTreeItem {
  id: number;
  serialNumber: string;
  name: string;
  nameAr: string;
  slug: string;
  editable: boolean;
  parentId?: number | null;
  level: number;
  balance: number;
  totalCredit: number;
  totalDebit: number;
  createdAt: string;
  updatedAt: string;
  children?: FinanceTreeItem[];
}

interface MainCategoryTotals {
  totalDebit: number;
  totalCredit: number;
  balance: number;
}

interface UseFinanceTreeExportProps {
  treeData: FinanceTreeItem[];
  mainCategoryTotals: MainCategoryTotals;
  totalItems: number;
  mainCategories: number;
  messageApi: MessageInstance;
}

export const useFinanceTreeExport = ({
  treeData,
  mainCategoryTotals,
  totalItems,
  mainCategories,
  messageApi,
}: UseFinanceTreeExportProps) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  // Function to flatten tree for Excel export with proper indentation
  const flattenTreeForExport = (items: FinanceTreeItem[], level = 1): any[] => {
    let result: any[] = [];
    items.forEach((item) => {
      const name = currentLang === "ar" ? item.nameAr : item.name;
      // Add indentation based on level (2 spaces per level)
      const indent = "  ".repeat(level - 1);
      result.push({
        Name: indent + name,
        SerialNumber: item.serialNumber,
        Level: level,
        TotalDebit: item.totalDebit,
        TotalCredit: -Math.abs(item.totalCredit), // Make credit negative
        Balance: item.balance,
      });
      if (item.children && item.children.length > 0) {
        result = result.concat(flattenTreeForExport(item.children, level + 1));
      }
    });
    return result;
  };

  const downloadExcel = () => {
    try {
      // Get current date for the report
      const reportDate = new Date().toLocaleDateString(currentLang === "ar" ? "ar-EG" : "en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      // Sheet 1: Summary Data with clean professional design
      const summaryData = [
        [t("label.menu.finance_tree"), ""],
        [t("label.finance_transaction.date") + ": " + reportDate, ""],
        ["", ""],
        ["", ""],
        [t("label.total_items"), totalItems],
        [t("label.main_categories"), mainCategories],
        ["", ""],
        [t("label.total_debit"), mainCategoryTotals.totalDebit],
        [t("label.total_credit"), -Math.abs(mainCategoryTotals.totalCredit)],
        ["", ""],
        [t("label.balance"), mainCategoryTotals.balance],
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

      // Merge title cells
      summarySheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }, // Title
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } }, // Date
      ];

      // Style title
      summarySheet["A1"] = {
        v: t("label.menu.finance_tree"),
        t: "s",
        s: {
          font: { bold: true, sz: 18, color: { rgb: "1F2937" } },
          fill: { fgColor: { rgb: "F3F4F6" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            bottom: { style: "thick", color: { rgb: "667EEA" } },
          },
        },
      };

      // Style date
      if (summarySheet["A2"]) {
        summarySheet["A2"].s = {
          font: { sz: 10, color: { rgb: "6B7280" }, italic: true },
          fill: { fgColor: { rgb: "F9FAFB" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            bottom: { style: "thin", color: { rgb: "E5E7EB" } },
          },
        };
      }

      // Style data rows with cleaner design
      const summaryRows = [
        { label: "A5", value: "B5", bgColor: "FFFFFF", labelColor: "374151", valueColor: "1F2937" },
        { label: "A6", value: "B6", bgColor: "F9FAFB", labelColor: "374151", valueColor: "1F2937" },
        { label: "A8", value: "B8", bgColor: "FEF3C7", labelColor: "92400E", valueColor: "78350F" },
        { label: "A9", value: "B9", bgColor: "FEE2E2", labelColor: "991B1B", valueColor: "DC2626" },
        {
          label: "A11",
          value: "B11",
          bgColor: mainCategoryTotals.balance >= 0 ? "D1FAE5" : "FEE2E2",
          labelColor: mainCategoryTotals.balance >= 0 ? "065F46" : "991B1B",
          valueColor: mainCategoryTotals.balance >= 0 ? "047857" : "DC2626",
        },
      ];

      summaryRows.forEach(({ label, value, bgColor, labelColor, valueColor }) => {
        if (summarySheet[label]) {
          summarySheet[label].s = {
            font: { bold: true, sz: 11, color: { rgb: labelColor } },
            fill: { fgColor: { rgb: bgColor } },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "E5E7EB" } },
              bottom: { style: "thin", color: { rgb: "E5E7EB" } },
              left: { style: "thin", color: { rgb: "E5E7EB" } },
              right: { style: "thin", color: { rgb: "E5E7EB" } },
            },
          };
        }
        if (summarySheet[value]) {
          summarySheet[value].s = {
            font: { bold: true, sz: 12, color: { rgb: valueColor } },
            fill: { fgColor: { rgb: bgColor } },
            alignment: { horizontal: "right", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "E5E7EB" } },
              bottom: { style: "thin", color: { rgb: "E5E7EB" } },
              left: { style: "thin", color: { rgb: "E5E7EB" } },
              right: { style: "thin", color: { rgb: "E5E7EB" } },
            },
            numFmt: "#,##0.00",
          };
        }
      });

      summarySheet["!cols"] = [{ wch: 28 }, { wch: 22 }];
      summarySheet["!rows"] = [
        { hpt: 35 }, // Title row height
        { hpt: 20 }, // Date row
        { hpt: 5 }, // Empty row
        { hpt: 10 }, // Empty row
      ];

      // Sheet 2: Finance Tree Data with clean professional tree structure
      const treeDataFlat = flattenTreeForExport(treeData);
      const treeSheet = XLSX.utils.json_to_sheet(treeDataFlat);

      // Apply styling to tree sheet
      const treeRange = XLSX.utils.decode_range(treeSheet["!ref"] || "A1");

      for (let R = treeRange.s.r; R <= treeRange.e.r; ++R) {
        for (let C = treeRange.s.c; C <= treeRange.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!treeSheet[cellAddress]) continue;

          // Header row styling - clean and professional
          if (R === 0) {
            treeSheet[cellAddress].s = {
              font: { bold: true, sz: 11, color: { rgb: "1F2937" } },
              fill: { fgColor: { rgb: "F3F4F6" } },
              alignment: { horizontal: "center", vertical: "center" },
              border: {
                top: { style: "medium", color: { rgb: "667EEA" } },
                bottom: { style: "medium", color: { rgb: "667EEA" } },
                left: { style: "thin", color: { rgb: "D1D5DB" } },
                right: { style: "thin", color: { rgb: "D1D5DB" } },
              },
            };
          } else {
            // Get the level for this row
            const levelCell = treeSheet[XLSX.utils.encode_cell({ r: R, c: 2 })];
            const level = levelCell ? levelCell.v : 1;

            // Clean alternating row colors with level distinction
            let bgColor = "FFFFFF";
            if (level === 1) {
              bgColor = R % 2 === 0 ? "EFF6FF" : "DBEAFE"; // Blue shades for level 1
            } else if (level === 2) {
              bgColor = R % 2 === 0 ? "F9FAFB" : "F3F4F6"; // Gray shades for level 2
            } else {
              bgColor = R % 2 === 0 ? "FFFFFF" : "FAFAFA"; // White shades for level 3+
            }

            // Base style for all cells
            const baseStyle: any = {
              fill: { fgColor: { rgb: bgColor } },
              alignment: { horizontal: C === 0 ? "left" : "right", vertical: "center" },
              border: {
                top: { style: "thin", color: { rgb: "E5E7EB" } },
                bottom: { style: "thin", color: { rgb: "E5E7EB" } },
                left: { style: "thin", color: { rgb: "E5E7EB" } },
                right: { style: "thin", color: { rgb: "E5E7EB" } },
              },
              numFmt: C >= 3 ? "#,##0.00" : undefined,
            };

            // Name column - bold for level 1, indent preserved in cell value
            if (C === 0) {
              treeSheet[cellAddress].s = {
                ...baseStyle,
                font: {
                  bold: level === 1,
                  sz: level === 1 ? 11 : 10,
                  color: { rgb: level === 1 ? "1F2937" : "374151" },
                },
              };
            }
            // Serial Number column
            else if (C === 1) {
              treeSheet[cellAddress].s = {
                ...baseStyle,
                font: { sz: 10, color: { rgb: "6B7280" } },
              };
            }
            // Level column
            else if (C === 2) {
              treeSheet[cellAddress].s = {
                ...baseStyle,
                font: { bold: true, sz: 10, color: { rgb: "667EEA" } },
                alignment: { horizontal: "center", vertical: "center" },
                fill: {
                  fgColor: {
                    rgb: level === 1 ? "DDD6FE" : level === 2 ? "E9D5FF" : "F3E8FF",
                  },
                },
              };
            }
            // Debit column (green)
            else if (C === 3) {
              treeSheet[cellAddress].s = {
                ...baseStyle,
                font: { sz: 10, color: { rgb: "047857" }, bold: level === 1 },
              };
            }
            // Credit column (red, negative)
            else if (C === 4) {
              treeSheet[cellAddress].s = {
                ...baseStyle,
                font: { sz: 10, color: { rgb: "DC2626" }, bold: level === 1 },
              };
            }
            // Balance column (conditional color and background)
            else if (C === 5) {
              const cellValue = treeSheet[cellAddress].v;
              const isNegative = typeof cellValue === "number" && cellValue < 0;
              treeSheet[cellAddress].s = {
                ...baseStyle,
                font: {
                  sz: 10,
                  bold: level === 1,
                  color: { rgb: isNegative ? "DC2626" : "047857" },
                },
                fill: { fgColor: { rgb: isNegative ? "FEE2E2" : level === 1 ? "D1FAE5" : bgColor } },
              };
            }
          }
        }
      }

      // Set column widths
      treeSheet["!cols"] = [
        { wch: 45 }, // Name (wider for indentation and tree structure)
        { wch: 18 }, // Serial Number
        { wch: 8 }, // Level
        { wch: 16 }, // Total Debit
        { wch: 16 }, // Total Credit
        { wch: 16 }, // Balance
      ];

      // Freeze header row for easy scrolling
      treeSheet["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft" };

      // Set row heights
      if (!treeSheet["!rows"]) treeSheet["!rows"] = [];
      treeSheet["!rows"][0] = { hpt: 28 }; // Header row height

      // Create workbook and add sheets with proper translated names
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, summarySheet, t("label.summary") || "Summary");
      XLSX.utils.book_append_sheet(
        workbook,
        treeSheet,
        t("label.menu.finance_tree") || "Finance Tree"
      );

      // Generate file and trigger download with translated name
      const timestamp = new Date().toISOString().split("T")[0];
      const fileName = `${t("label.menu.finance_tree")}_${timestamp}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      messageApi.success(t("message.success.file_downloaded"));
    } catch (error) {
      console.error("Error downloading Excel:", error);
      messageApi.error(t("message.error.download_failed") || "Failed to download file");
    }
  };

  return { downloadExcel };
};

